/**
 * API klienti. Har bir so'rovga `Authorization: tma <initData>` qo'shiladi —
 * serverdagi HMAC tekshiruvi shunga tayanadi (TZ §13.2).
 */

import { mockApi } from "./mock";
import { getInitData } from "./telegram";
import type {
  EventRequest,
  FeedResponse,
  HomeResponse,
  MeResponse,
  OnboardingRequest,
  OnboardingResponse,
  OpportunityCard,
  LikeResponse,
  OpportunityDetail,
  SaveResponse,
  StartParamResponse,
  TaxonomyResponse,
} from "./types";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** initData yaroqsiz — odatda Mini App'ni qayta ochish kerak. */
  get isAuth(): boolean {
    return this.status === 401;
  }

  get isRateLimit(): boolean {
    return this.status === 429;
  }
}

/*
 * TEZLIK CHEKLOVI. Server bitta foydalanuvchiga daqiqasiga 120 so'rov
 * beradi (`config.RATE_LIMIT_REQUESTS`, siljuvchi oyna) va oshsa 429
 * qaytaradi.
 *
 * 2026-11-04 da Telegram Desktop'da uzun qidiruv ro'yxati ustidan
 * sichqoncha yurgizilganda har karta grant sahifasini oldindan yuklab
 * (`prefetchDetail`) byudjetni yeb qo'ydi: server 429 berdi va
 * "Ko'rishni davom ettirish" birinchi bosishda "Qidiruv ishlamadi"
 * dedi, ikkinchisida ishladi. Ikki to'siq:
 *   1. oldindan yuklash — eng arzon iste'molchi: daqiqasiga
 *      `PREFETCH_PER_MINUTE` tadan oshmaydi va 429 dan keyin
 *      `RATE_PAUSE_MS` davomida umuman ishlamaydi;
 *   2. haqiqiy GET so'rov 429 olsa, biroz kutib o'zi qayta uriniladi
 *      (`RETRY_DELAYS_MS`) — odam xato ko'rmaydi.
 */
const PREFETCH_PER_MINUTE = 20;
const RATE_PAUSE_MS = 60_000;
const RETRY_DELAYS_MS = [1500, 3000];
let rateLimitedUntil = 0;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function request<T>(
  path: string,
  init: RequestInit = {},
  // Oldindan yuklash qayta urinmaydi: u shoshilinch emas va byudjetni
  // yana yeb qo'yardi
  retry = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `tma ${getInitData()}`);
  if (init.body) headers.set("Content-Type", "application/json");

  const idempotent = (init.method ?? "GET").toUpperCase() === "GET";
  let response: Response;
  for (let attempt = 0; ; attempt++) {
    try {
      response = await fetch(`${BASE}${path}`, { ...init, headers });
    } catch {
      throw new ApiError(0, "Tarmoqqa ulanib bo'lmadi");
    }
    if (response.status !== 429) break;
    rateLimitedUntil = Date.now() + RATE_PAUSE_MS;
    // Saqlash/yoqtirish kabi POST qayta yuborilmaydi — ikki marta
    // bajarilib qolishi mumkin. Ularning o'z xato xabari bor.
    if (!retry || !idempotent || attempt >= RETRY_DELAYS_MS.length) break;
    await sleep(RETRY_DELAYS_MS[attempt]);
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let detail = `Xatolik (${response.status})`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
    } catch {
      /* javob JSON emas — umumiy xabar qoladi */
    }
    throw new ApiError(response.status, detail);
  }

  return (await response.json()) as T;
}

export interface SearchQuery {
  q?: string;
  type?: string;
  degree?: string;
  funding?: string;
  country?: string;
  /** Qit'a: europe | asia | africa | north_america | south_america | oceania */
  continent?: string;
  field?: string;
  format?: string;
  no_ielts?: boolean;
  no_experience?: boolean;
  no_fee?: boolean;
  deadline_within?: number;
  sort?: "match" | "deadline" | "newest";
  offset?: number;
  limit?: number;
}

export function searchQueryToParams(query: SearchQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  return params;
}

// ------------------------------------------------------------ SWR kesh
const cache = new Map<string, { data: unknown; ts: number }>();
const CACHE_TTL = 3 * 60 * 1000; // 3 daqiqa
const MAX_CACHE_SIZE = 50; // Kesh hajmi chegarasi

export function getCached<T>(key: string): T | null {
  const item = cache.get(key);
  if (!item) return null;
  if (Date.now() - item.ts > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return item.data as T;
}

export function setCached<T>(key: string, data: T): void {
  if (cache.size >= MAX_CACHE_SIZE) {
    const firstKey = cache.keys().next().value;
    if (firstKey) cache.delete(firstKey);
  }
  cache.set(key, { data, ts: Date.now() });
}

export function invalidateCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

// Oldindan yuklash: oxirgi bir daqiqadagi vaqtlar va hozir ketayotganlar
// (bitta grant ikki marta so'ralmasin — loglarda shunday juftlar bor edi)
const prefetchTimes: number[] = [];
const prefetching = new Set<number>();

const realApi = {
  start: () => request<StartParamResponse>("/api/start"),
  me: () => request<MeResponse>("/api/me"),

  home: async (): Promise<HomeResponse> => {
    const res = await request<HomeResponse>("/api/home");
    setCached("home", res);
    return res;
  },

  taxonomy: () => request<TaxonomyResponse>("/api/taxonomy"),

  onboarding: (payload: OnboardingRequest) =>
    request<OnboardingResponse>("/api/onboarding", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  search: async (query: SearchQuery): Promise<FeedResponse> => {
    const key = `search:${searchQueryToParams(query).toString()}`;
    const res = await request<FeedResponse>(`/api/opportunities?${searchQueryToParams(query)}`);
    setCached(key, res);
    return res;
  },

  detail: async (
    id: number,
    source?: string | null,
    prefetch = false,
  ): Promise<OpportunityDetail> => {
    const key = `detail:${id}`;
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    if (prefetch) params.set("prefetch", "true");
    const qs = params.toString();
    const res = await request<OpportunityDetail>(
      `/api/opportunities/${id}${qs ? `?${qs}` : ""}`,
      {},
      !prefetch,
    );
    setCached(key, res);
    return res;
  },

  similar: (id: number) =>
    request<OpportunityCard[]>(`/api/opportunities/${id}/similar`),

  save: async (id: number, remind = true) => {
    const res = await request<SaveResponse>(
      `/api/opportunities/${id}/save?remind=${remind}`,
      { method: "POST" },
    );
    invalidateCache("saved");
    invalidateCache(`detail:${id}`);
    invalidateCache("home");
    return res;
  },

  unsave: async (id: number) => {
    const res = await request<SaveResponse>(`/api/opportunities/${id}/save`, { method: "DELETE" });
    invalidateCache("saved");
    invalidateCache(`detail:${id}`);
    invalidateCache("home");
    return res;
  },

  like: async (id: number) => {
    const res = await request<LikeResponse>(`/api/opportunities/${id}/like`, { method: "POST" });
    invalidateCache(`detail:${id}`);
    return res;
  },

  unlike: async (id: number) => {
    const res = await request<LikeResponse>(`/api/opportunities/${id}/like`, { method: "DELETE" });
    invalidateCache(`detail:${id}`);
    return res;
  },

  saved: async (offset = 0, limit = 20): Promise<FeedResponse> => {
    const key = `saved:${offset}:${limit}`;
    const res = await request<FeedResponse>(`/api/saved?offset=${offset}&limit=${limit}`);
    setCached(key, res);
    return res;
  },

  prefetchDetail: (id: number, source?: string | null) => {
    const key = `detail:${id}`;
    if (getCached(key) || prefetching.has(id)) return;
    const now = Date.now();
    if (now < rateLimitedUntil) return;
    while (prefetchTimes.length && prefetchTimes[0] < now - 60_000) prefetchTimes.shift();
    if (prefetchTimes.length >= PREFETCH_PER_MINUTE) return;
    prefetchTimes.push(now);
    prefetching.add(id);
    void realApi
      .detail(id, source, true)
      .catch(() => {})
      .finally(() => prefetching.delete(id));
  },

  track: (event: EventRequest) =>
    request<void>("/api/events", {
      method: "POST",
      body: JSON.stringify(event),
    }).catch(() => {
      // Analitika hech qachon foydalanuvchi oqimini to'xtatmasligi kerak
    }),
};

// Dev-mock rejimi (`npm run dev:mock`) — backend va Postgres'siz interfeysni
// ko'rish uchun. Ishlab chiqarishda VITE_MOCK doimiy "0", shuning uchun
// Vite bu shoxni va mock.ts ni butunlay tashlab ketadi.
export const api = (import.meta.env.VITE_MOCK === "1"
  ? (mockApi as unknown as typeof realApi)
  : realApi);
